import type {
	SavedSegment,
	SavedSegmentListResponse,
	SaveSegmentRequest,
	UpdateSavedSegmentRequest,
} from "@/shared/api/generated/types/marketing"

import { marketingSavedSegmentsFixture } from "./marketing-segment-fixtures"

const STORAGE_KEY = "admin-ui.msw.marketing-saved-segments.v1"
const DEFAULT_BUSINESS_ID = "biz_123"

type SegmentsByBusiness = Map<string, SavedSegment[]>

function canUseSessionStorage(): boolean {
	try {
		return typeof sessionStorage !== "undefined"
	} catch {
		return false
	}
}

function cloneSegments(segments: SavedSegment[]): SavedSegment[] {
	return segments.map((segment) => ({
		...segment,
		rules: segment.rules.map((rule) => ({ ...rule })),
	}))
}

function loadSegmentsFromStorage(): SegmentsByBusiness | null {
	if (!canUseSessionStorage()) {
		return null
	}

	try {
		const raw = sessionStorage.getItem(STORAGE_KEY)
		if (!raw) {
			return null
		}

		const parsed = JSON.parse(raw) as Record<string, SavedSegment[]>
		if (!parsed || typeof parsed !== "object") {
			return null
		}

		return new Map(
			Object.entries(parsed).map(([businessId, segments]) => [
				businessId,
				cloneSegments(segments),
			]),
		)
	} catch {
		return null
	}
}

function persistSegments(store: SegmentsByBusiness): void {
	if (!canUseSessionStorage()) {
		return
	}

	const payload = Object.fromEntries(store.entries())
	sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
}

function createInitialStore(): SegmentsByBusiness {
	return new Map([
		[DEFAULT_BUSINESS_ID, cloneSegments(marketingSavedSegmentsFixture.data)],
	])
}

const storedSegments = loadSegmentsFromStorage()
const segmentsByBusiness = storedSegments ?? createInitialStore()

let idCounter = Math.max(
	100,
	...[...segmentsByBusiness.values()].flatMap((segments) =>
		segments.map((segment) => {
			const match = segment.id.match(/^seg-(\d+)$/)
			return match ? Number(match[1]) : 0
		}),
	),
)

function nextSegmentId(): string {
	idCounter += 1
	return `seg-${idCounter}`
}

function resolveBusinessId(businessId?: string): string {
	return businessId || DEFAULT_BUSINESS_ID
}

function getBusinessSegments(businessId: string): SavedSegment[] {
	const resolvedBusinessId = resolveBusinessId(businessId)
	if (!segmentsByBusiness.has(resolvedBusinessId)) {
		segmentsByBusiness.set(resolvedBusinessId, [])
	}

	return segmentsByBusiness.get(resolvedBusinessId) ?? []
}

export function listSavedSegments(
	businessId: string,
): SavedSegmentListResponse {
	const data = getBusinessSegments(businessId)

	return {
		data: cloneSegments(data),
		paging: {
			page: 1,
			pageSize: 20,
			total: data.length,
		},
	}
}

export function createSavedSegment(body: SaveSegmentRequest): SavedSegment {
	const businessId = resolveBusinessId()
	const segments = getBusinessSegments(businessId)
	const segment: SavedSegment = {
		id: nextSegmentId(),
		name: body.name,
		rules: body.rules.map((rule) => ({ ...rule })),
		rulesMatch: body.rulesMatch,
	}

	segments.push(segment)
	persistSegments(segmentsByBusiness)

	return { ...segment, rules: segment.rules.map((rule) => ({ ...rule })) }
}

export function updateSavedSegment(
	segmentId: string,
	businessId: string,
	body: UpdateSavedSegmentRequest,
): SavedSegment | null {
	const segments = getBusinessSegments(businessId)
	const index = segments.findIndex((segment) => segment.id === segmentId)
	if (index < 0) {
		return null
	}

	const updated: SavedSegment = {
		...segments[index],
		name: body.name,
		rules: body.rules.map((rule) => ({ ...rule })),
		rulesMatch: body.rulesMatch,
	}

	segments[index] = updated
	persistSegments(segmentsByBusiness)

	return {
		...updated,
		rules: updated.rules.map((rule) => ({ ...rule })),
	}
}

export function deleteSavedSegment(
	segmentId: string,
	businessId: string,
): boolean {
	const segments = getBusinessSegments(businessId)
	const nextSegments = segments.filter((segment) => segment.id !== segmentId)
	if (nextSegments.length === segments.length) {
		return false
	}

	segmentsByBusiness.set(resolveBusinessId(businessId), nextSegments)
	persistSegments(segmentsByBusiness)

	return true
}

export function getSavedSegmentById(
	segmentId: string,
	businessId: string,
): SavedSegment | null {
	return (
		getBusinessSegments(businessId).find(
			(segment) => segment.id === segmentId,
		) ?? null
	)
}
